import React, {useState, useEffect} from 'react';
import {API} from 'aws-amplify';
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import {ENDPOINT} from '../../constants';

const filter = createFilterOptions();

export default function AutoCompleteTags({tags, setTags}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, toggleDialogOpen] = useState(false);
  const [userGroupTags, setUserGroupTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');

  const fetchTags = () => {
    setIsLoading(true);
    return API.get(ENDPOINT, `/tags`)
      .then(setUserGroupTags)
      .then(() => {
        setIsLoading(false);
      });
  };

  // Fetch on load
  useEffect(() => {
    fetchTags();
  }, []);

  function handleTagsChange(event, newValue, reason) {
    const newTag = newValue[newValue.length - 1];

    if (reason === 'selectOption' && newTag.inputValue) {
      setNewTagName(newTag.inputValue);
      toggleDialogOpen(true);
    } else {
      setTags(newValue);
    }
  }

  function handleDialogClose() {
    setNewTagName('');
    toggleDialogOpen(false);
  }

  async function handleDialogSubmitForm(event) {
    event.preventDefault();

    try {
      const request = {
        body: {
          name: newTagName,
        },
      };
      const newTag = await API.post(ENDPOINT, '/tags', request);
      setUserGroupTags([...userGroupTags, newTag]);
      setTags([...tags, newTag]);
      toggleDialogOpen(false);
    } catch (error) {
      console.log(error);
      // TODO: Error handling, like "tag already exists"
    }
  }

  return (
    <>
      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <form onSubmit={handleDialogSubmitForm}>
          <DialogTitle>Add a new tag</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              value={newTagName}
              onChange={(event) => setNewTagName(event.target.value)}
              label="Tag"
              type="text"
              variant="standard"
              required
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button type="submit">Add</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Autocomplete
        multiple
        options={userGroupTags}
        value={tags}
        onChange={handleTagsChange}
        disabled={isLoading}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderOption={(props, option) => <li {...props}>{option.name}</li>}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label="Begin typing..."
            placeholder="Tags"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const {inputValue} = params;
          // Suggest the creation of a new tag
          const isExisting = options.some((option) => inputValue === option.name);
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              inputValue,
              name: `Add "${inputValue}"`,
            });
          }
          return filtered;
        }}
      />
    </>
  );
}
